/**
 * Admin/Owner Approvals Queue for Old Gold shortage overrides.
 * Lists all pieces with `shortage_override_request.status='pending'` and lets
 * admin approve (finalise piece) or reject with a note.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Check, X, AlertTriangle, Image as ImgIcon } from 'lucide-react';
import { CATEGORY_LABEL, fmt } from './oldgold/constants';
import { errMsg } from './oldgold/errors';

const REASON_LABEL = {
  fell_during_cutting: 'Fell during cutting / pliers work',
  crushed_lost: 'Crushed and lost while breaking',
  missing_on_unpacking: 'Missing on unpacking',
  original_miscount_at_receipt: 'Original count wrong at receipt',
  damaged_beyond_recognition: 'Damaged beyond recognition',
  other: 'Other',
};

const ShortageStrip = ({ shortage }) => (
  <div className="grid grid-cols-3 gap-2">
    {['diamond', 'stone', 'beads_pearl'].map((c) => {
      const s = (shortage || {})[c] || 0;
      const tone = s > 0
        ? 'bg-rose-50 border-rose-200 text-rose-800'
        : 'bg-slate-50 border-slate-200 text-slate-500';
      return (
        <div key={c} className={`rounded-lg border py-1.5 text-center ${tone}`}>
          <div className="text-[10px] uppercase tracking-wide opacity-80">{CATEGORY_LABEL[c]}</div>
          <div className="text-sm font-semibold">{s > 0 ? `${s} short` : 'OK'}</div>
        </div>
      );
    })}
  </div>
);

const OldGoldApprovals = () => {
  const { currentUser } = useAuth();
  const isAdmin = ['admin', 'owner'].includes(currentUser?.role);

  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(false);

  // Approve / reject dialog state
  const [selected, setSelected] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/old-gold/approvals');
      setPieces(r.data);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load approvals'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const approve = async (piece) => {
    if (!window.confirm(`Approve override for ${piece.serial_no}? Piece will be finalised and can't be edited.`)) return;
    setBusy(true);
    try {
      await api.post(`/old-gold/approvals/${piece.serial_no}/approve`);
      toast.success(`${piece.serial_no} approved and finalised`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to approve'));
    } finally {
      setBusy(false);
    }
  };

  const openReject = (piece) => {
    setSelected(piece);
    setRejectNote('');
    setRejectOpen(true);
  };

  const reject = async () => {
    if (!selected) return;
    if (rejectNote.trim().length < 6) return toast.error('Add a short reason (min 6 chars) for the MIA.');
    setBusy(true);
    try {
      await api.post(`/old-gold/approvals/${selected.serial_no}/reject`, { note: rejectNote.trim() });
      toast.success(`${selected.serial_no} sent back to MIA`);
      setRejectOpen(false);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to reject'));
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <div className="text-slate-500">Only admin/owner can view approvals.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50/40 pb-20">
      <header className="bg-gradient-to-r from-amber-800 to-yellow-700 text-white shadow sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />
            <h1 className="text-base sm:text-xl font-semibold" data-testid="oldgold-approvals-title">
              Shortage Approvals
            </h1>
          </div>
          <GlobalNavigationMenu />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 sm:px-4 py-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500 mr-auto">
            {pieces.length} piece{pieces.length !== 1 ? 's' : ''} awaiting approval
          </div>
          <Button variant="outline" size="icon" onClick={load} className="h-10 w-10" data-testid="approvals-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {loading && (
          <div className="text-center text-slate-400 py-8 text-sm">Loading…</div>
        )}
        {!loading && pieces.length === 0 && (
          <div className="bg-white rounded-xl border border-emerald-100 p-8 text-center" data-testid="approvals-empty">
            <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <div className="text-slate-600 text-sm">All clear — no shortage overrides pending.</div>
          </div>
        )}

        {!loading && pieces.map((p) => {
          const req = p.shortage_override_request || {};
          return (
            <div key={p._id} className="bg-white rounded-xl shadow border border-amber-100 p-3 space-y-3" data-testid={`approval-card-${p.serial_no}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold">{p.serial_no}</span>
                <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                <span className="text-xs text-slate-500 ml-auto">
                  {p.branch} · {fmt(p.gross_weight)} gm · {p.lot_no}
                </span>
              </div>

              <ShortageStrip shortage={req.shortage} />

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs space-y-1">
                <div><b>Reason:</b> {REASON_LABEL[req.reason] || req.reason || '—'}</div>
                <div><b>By:</b> {req.requested_by_name || 'MIA'} · {req.requested_at ? new Date(req.requested_at).toLocaleString() : ''}</div>
                {req.note && <div><b>Note:</b> {req.note}</div>}
              </div>

              {req.photo_url && (
                <a href={req.photo_url} target="_blank" rel="noreferrer" className="block relative w-full h-40 rounded-lg overflow-hidden border">
                  <img src={req.photo_url} alt="evidence" className="w-full h-full object-cover" />
                  <div className="absolute top-1 right-1 bg-black/50 text-white text-[10px] rounded px-1.5 py-0.5 flex items-center gap-1">
                    <ImgIcon className="w-3 h-3" /> tap to enlarge
                  </div>
                </a>
              )}

              {/* Draft breakage snapshot */}
              {p.breakage_draft && (
                <div className="border rounded-lg p-2 text-[11px] text-slate-600">
                  Draft: Net {fmt(p.breakage_draft.current_net_weight)} + Worker {fmt(p.breakage_draft.worker_gold_wt)} = Net Gold {fmt((p.breakage_draft.current_net_weight || 0) + (p.breakage_draft.worker_gold_wt || 0))} gm
                  {(p.breakage_draft.stone_entries || []).length > 0 && (
                    <div className="mt-1">
                      {(p.breakage_draft.stone_entries || []).map((e, i) => (
                        <span key={i} className="mr-2">
                          {CATEGORY_LABEL[e.category] || e.category}: {e.pcs || 0}p · {fmt(e.wt)}g
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1 h-11"
                  onClick={() => openReject(p)}
                  disabled={busy}
                  data-testid={`approval-reject-${p.serial_no}`}
                >
                  <X className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button
                  className="flex-1 h-11 bg-emerald-700 hover:bg-emerald-800"
                  onClick={() => approve(p)}
                  disabled={busy}
                  data-testid={`approval-approve-${p.serial_no}`}
                >
                  <Check className="w-4 h-4 mr-1" /> Approve &amp; Finalise
                </Button>
              </div>
            </div>
          );
        })}
      </main>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-800">
              <AlertTriangle className="w-5 h-5" /> Reject Override
            </DialogTitle>
            <DialogDescription>
              This sends the piece back to the MIA with your note. They can recount and re-submit.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. Recount tray 3 first — I saw stones there."
              className="text-base"
              data-testid="reject-note-input"
              autoFocus
            />
            <div className="text-[10px] text-slate-500 mt-1">Min 6 characters.</div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="flex-1 h-11">Cancel</Button>
            <Button
              variant="destructive"
              onClick={reject}
              disabled={busy || rejectNote.trim().length < 6}
              className="flex-1 h-11"
              data-testid="reject-confirm-btn"
            >
              <X className="w-4 h-4 mr-1" /> {busy ? 'Sending…' : 'Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OldGoldApprovals;
